import { sortByName } from '../../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import {
  TrashIcon,
  CheckCircleIcon,
  NoSymbolIcon, PencilIcon, AcademicCapIcon, MagnifyingGlassIcon,
  EnvelopeIcon, BuildingOfficeIcon, ChevronLeftIcon, ChevronRightIcon,
  FunnelIcon, XMarkIcon, CheckIcon, IdentificationIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .st-root * { box-sizing:border-box; }
  .st-root { font-family:'Sora',sans-serif; background:#f0f2f7; min-height:100vh; color:#1a1d2e; padding:2rem; }
  .st-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.75rem; flex-wrap:wrap; gap:1rem; }
  .st-title { font-size:1.75rem; font-weight:700; letter-spacing:-0.03em; color:#0f1123; }
  .st-subtitle { font-size:0.82rem; color:#8b90a7; margin-top:2px; }
  .toolbar { display:flex; align-items:center; gap:10px; margin-bottom:1.25rem; flex-wrap:wrap; }
  .search-wrap { position:relative; display:flex; align-items:center; }
  .search-wrap > svg { position:absolute; left:12px; width:15px; height:15px; color:#9094a8; stroke-width:2; pointer-events:none; }
  .search-input { font-family:'Sora',sans-serif; font-size:0.82rem; padding:9px 14px 9px 36px; border:1.5px solid #e4e7f0; border-radius:10px; background:white; color:#1a1d2e; outline:none; width:230px; transition:border-color .2s,box-shadow .2s; }
  .search-input:focus { border-color:#10c98e; box-shadow:0 0 0 3px rgba(16,201,142,0.1); }
  .search-input::placeholder { color:#c0c5dc; }
  .filter-wrap { position:relative; display:flex; align-items:center; }
  .filter-wrap > svg { position:absolute; left:11px; width:14px; height:14px; color:#9094a8; stroke-width:2; pointer-events:none; }
  .filter-select { font-family:'Sora',sans-serif; font-size:0.82rem; padding:9px 14px 9px 32px; border:1.5px solid #e4e7f0; border-radius:10px; background:white; color:#1a1d2e; outline:none; cursor:pointer; appearance:none; min-width:180px; transition:border-color .2s,box-shadow .2s; }
  .filter-select:focus { border-color:#10c98e; box-shadow:0 0 0 3px rgba(16,201,142,0.1); }
  .summary-row { display:flex; gap:10px; margin-bottom:1.25rem; flex-wrap:wrap; }
  .summary-pill { display:flex; align-items:center; gap:8px; background:white; border:1px solid #e8ebf4; border-radius:10px; padding:8px 16px; font-size:0.78rem; color:#6b7194; font-weight:500; }
  .summary-pill strong { color:#0f1123; font-size:0.88rem; font-weight:700; }
  .pill-dot { width:8px; height:8px; border-radius:50%; }
  .table-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; animation:fadeUp .4s ease both; margin-bottom:1rem; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  .teachers-table { width:100%; border-collapse:collapse; }
  .teachers-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .teachers-table th { padding:12px 20px; text-align:center !important; font-size:0.7rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; }
  .teachers-table th:last-child { text-align:right; }
  .teachers-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .15s; }
  .teachers-table tbody tr:last-child { border-bottom:none; }
  .teachers-table tbody tr:hover { background:#fafbff; }
  .teachers-table td { padding:14px 20px; vertical-align:middle; text-align:center !important; }
  .teachers-table td:last-child { text-align:right; }
  .name-cell { display:flex; align-items:center; gap:11px; }
  .avatar { width:36px; height:36px; border-radius:10px; background:#ecfdf5; color:#10c98e; display:flex; align-items:center; justify-content:center; flex-shrink:0; font-size:0.82rem; font-weight:700; text-transform:uppercase; }
  .name-text { font-size:0.85rem; font-weight:600; color:#1a1d2e; }
  .info-cell { display:flex; align-items:center; gap:6px; font-size:0.8rem; color:#6b7194; }
  .info-cell svg { width:13px; height:13px; color:#b0b5cc; stroke-width:1.8; flex-shrink:0; }
  .mono { font-family:'JetBrains Mono',monospace; font-size:0.78rem; }
  .chip-id { font-family:'JetBrains Mono',monospace; font-size:0.72rem; font-weight:600; padding:3px 10px; border-radius:99px; background:#fffbeb; color:#f59e0b; display:inline-block; }
  .subjects-cell { display:flex; flex-wrap:wrap; gap:5px; }
  .subject-tag { font-size:0.68rem; font-weight:600; padding:2px 9px; border-radius:99px; background:#ecfdf5; color:#10c98e; }
  .subject-tag-more { font-size:0.68rem; font-weight:600; padding:2px 9px; border-radius:99px; background:#f3f5fb; color:#9094a8; }
  .action-btns { display:inline-flex; align-items:center; gap:6px; }
  .btn-edit { width:32px; height:32px; display:inline-flex; align-items:center; justify-content:center; border-radius:8px; border:1px solid #e4e7f0; background:#f7f8fc; color:#6b7194; cursor:pointer; transition:all .15s; }
  .btn-edit:hover { background:#ecfdf5; border-color:#10c98e; color:#10c98e; transform:scale(1.05); }
  .btn-edit svg { width:14px; height:14px; stroke-width:2; }

  .btn-toggle { width:28px; height:28px; border-radius:7px; border:1px solid #e4e7f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; font-size:.72rem; font-weight:700; }
  .btn-toggle svg { width:13px; height:13px; stroke-width:2; }
  .btn-toggle.deactivate { color:#ef4444; border-color:#fecaca; background:#fef2f2; }
  .btn-toggle.deactivate:hover { background:#ef4444; color:white; border-color:#ef4444; }
  .btn-toggle.activate   { color:#10c98e; border-color:#a7f3d0; background:#ecfdf5; }
  .btn-toggle.activate:hover   { background:#10c98e; color:white; border-color:#10c98e; }
  .btn-toggle:disabled { opacity:.5; cursor:not-allowed; }
  .btn-delete { width:32px; height:32px; display:inline-flex; align-items:center; justify-content:center; border-radius:8px; border:1px solid #fee2e2; background:#fef2f2; color:#ef4444; cursor:pointer; transition:all .15s; }
  .btn-delete:hover { background:#ef4444; border-color:#ef4444; color:white; transform:scale(1.05); }
  .btn-delete svg { width:14px; height:14px; stroke-width:2; }
  .empty-state { text-align:center; padding:4rem 2rem; }
  .empty-icon { width:52px; height:52px; border-radius:14px; background:#ecfdf5; color:#10c98e; display:flex; align-items:center; justify-content:center; margin:0 auto 14px; }
  .empty-icon svg { width:24px; height:24px; stroke-width:1.6; }
  .empty-title { font-size:0.92rem; font-weight:600; color:#1a1d2e; margin-bottom:5px; }
  .empty-sub { font-size:0.77rem; color:#9094a8; }
  .pagination { display:flex; align-items:center; justify-content:space-between; background:white; border:1px solid #e8ebf4; border-radius:12px; padding:12px 20px; }
  .page-info { font-family:'JetBrains Mono',monospace; font-size:0.78rem; color:#9094a8; }
  .page-info strong { color:#1a1d2e; }
  .page-btns { display:flex; gap:6px; }
  .page-btn { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border:1.5px solid #e4e7f0; border-radius:8px; background:white; font-family:'Sora',sans-serif; font-size:0.78rem; font-weight:500; color:#6b7194; cursor:pointer; transition:all .15s; }
  .page-btn svg { width:14px; height:14px; stroke-width:2.2; }
  .page-btn:hover:not(:disabled) { border-color:#10c98e; color:#10c98e; background:#ecfdf5; }
  .page-btn:disabled { opacity:.35; cursor:not-allowed; }
  .modal-overlay { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:50; animation:overlayIn .2s ease; }
  @keyframes overlayIn { from{opacity:0} to{opacity:1} }
  .modal-card { background:white; border-radius:20px; padding:2rem; width:100%; max-width:440px; margin:1rem; border:1px solid #e8ebf4; animation:modalIn .25s ease; }
  @keyframes modalIn { from{opacity:0;transform:translateY(16px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
  .modal-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.5rem; }
  .modal-title { font-size:1.05rem; font-weight:700; color:#0f1123; letter-spacing:-0.02em; }
  .modal-sub { font-size:0.76rem; color:#9094a8; margin-top:2px; }
  .modal-close { width:32px; height:32px; border-radius:8px; border:1px solid #e8ebf4; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#9094a8; transition:all .15s; }
  .modal-close:hover { background:#f3f5fb; color:#1a1d2e; }
  .modal-close svg { width:16px; height:16px; }
  .field { margin-bottom:1rem; }
  .field label { display:block; font-size:0.75rem; font-weight:500; color:#6b7194; text-transform:uppercase; letter-spacing:.05em; margin-bottom:6px; }
  .field input { width:100%; padding:10px 14px; border:1.5px solid #e4e7f0; border-radius:10px; font-family:'Sora',sans-serif; font-size:0.84rem; color:#1a1d2e; background:#fafbff; outline:none; transition:border-color .2s,box-shadow .2s; }
  .field input:focus { border-color:#10c98e; box-shadow:0 0 0 3px rgba(16,201,142,0.1); background:white; }
  .field input::placeholder { color:#c0c5dc; }
  .toggle-row { display:flex; align-items:center; justify-content:space-between; padding:12px 0; border-top:1px solid #f3f5fb; margin-top:4px; }
  .toggle-label { font-size:0.84rem; font-weight:500; color:#1a1d2e; }
  .toggle-sub { font-size:0.74rem; color:#9094a8; margin-top:1px; }
  .toggle { position:relative; width:40px; height:22px; background:#e4e7f0; border-radius:99px; cursor:pointer; border:none; transition:background .25s; flex-shrink:0; }
  .toggle.on { background:#10c98e; }
  .toggle::after { content:''; position:absolute; top:3px; left:3px; width:16px; height:16px; border-radius:50%; background:white; transition:transform .25s; box-shadow:0 1px 3px rgba(0,0,0,.2); }
  .toggle.on::after { transform:translateX(18px); }
  .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:1.5rem; }
  .btn-ghost { padding:10px 18px; background:#f3f5fb; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:0.82rem; font-weight:500; color:#6b7194; cursor:pointer; transition:background .15s; }
  .btn-ghost:hover { background:#e8ebf4; }
  .btn-primary { display:inline-flex; align-items:center; gap:7px; padding:10px 18px; background:#10c98e; color:white; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:0.82rem; font-weight:600; cursor:pointer; transition:all .2s; }
  .btn-primary:hover { background:#0db87e; transform:translateY(-1px); box-shadow:0 6px 20px rgba(16,201,142,.3); }
  .btn-primary:disabled { opacity:.6; cursor:not-allowed; transform:none; box-shadow:none; }
  .btn-primary svg { width:15px; height:15px; stroke-width:2.2; }
  .loading-wrap { display:flex; align-items:center; justify-content:center; height:60vh; color:#9094a8; font-size:0.9rem; gap:10px; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }

  /* Mobile cards */
  .st-cards { display:none; padding:10px; }
  .st-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .st-card-top { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .st-card-row { display:flex; justify-content:space-between; align-items:flex-start; font-size:.78rem; padding:5px 0; border-bottom:1px solid #f3f5fb; gap:8px; }
  .st-card-row:last-of-type { border-bottom:none; }
  .st-card-label { color:#9094a8; font-weight:600; font-size:.68rem; text-transform:uppercase; letter-spacing:.05em; flex-shrink:0; padding-top:2px; }
  .st-card-actions { display:flex; gap:6px; margin-top:10px; }
  @media (max-width:768px) {
    .st-tbl-wrap { display:none !important; }
    .st-cards { display:block; }
  }
`;

const initials = (name = '') => name.split(' ').slice(0, 2).map((w) => w[0]).join('') || '?';
const MAX_SUBJECTS = 3;

export default function SuperTeachers() {
  const [teachers, setTeachers]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [page, setPage]                     = useState(1);
  const [totalPages, setTotalPages]         = useState(1);
  const [totalTeachers, setTotalTeachers]   = useState(0);
  const [schools, setSchools]               = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [search, setSearch]                 = useState('');
  const [editTarget, setEditTarget]         = useState(null);
  const [editData, setEditData]             = useState({ name: '', email: '', isActive: true });
  const [submitting, setSubmitting]         = useState(false);
  const [toggling,   setToggling]           = useState({});

  useEffect(() => { fetchSchools(); }, []);
  useEffect(() => { fetchTeachers(); }, [page, selectedSchool]);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 10 });
      if (selectedSchool) params.append('school', selectedSchool);
      const { data } = await api.get(`/super/teachers?${params}`);
      setTeachers(data.teachers || []);
      setTotalPages(Math.max(1, data.totalPages || 1));
      setTotalTeachers(data.total || data.teachers?.length || 0);
    } catch { toast.error('Failed to fetch teachers'); }
    finally { setLoading(false); }
  };

  const fetchSchools = async () => {
    try { const { data } = await api.get('/super/schools'); setSchools(data); }
    catch { console.error('Failed to fetch schools'); }
  };

  const handleToggle = async (userId) => {
    if (!userId) return;
    setToggling(p => ({...p, [userId]: true}));
    try {
      const { data } = await api.put(`/super/users/${userId}/toggle-active`);
      toast.success(data.message);
      fetchTeachers();
    } catch { toast.error('Failed to update'); }
    finally { setToggling(p => ({...p, [userId]: false})); }
  };

  const openEdit = (teacher) => {
    setEditTarget(teacher);
    setEditData({ name: teacher.user?.name || '', email: teacher.user?.email || '', isActive: teacher.user?.isActive ?? true });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/super/users/${editTarget.user?._id}`, editData);
      toast.success('Teacher updated');
      setEditTarget(null);
      fetchTeachers();
    } catch { toast.error('Failed to update teacher'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete teacher "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/super/users/${userId}`);
      toast.success('Teacher deleted');
      if (teachers.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchTeachers();
    } catch { toast.error('Delete failed'); }
  };

  const handleSchoolFilter = (val) => { setSelectedSchool(val); setPage(1); };

  const filtered = teachers.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.user?.name?.toLowerCase().includes(q) || t.user?.email?.toLowerCase().includes(q) || t.school?.name?.toLowerCase().includes(q) || t.employeeId?.toLowerCase().includes(q) || t.subjects?.some((s) => s.toLowerCase().includes(q));
  });

  if (loading) return <div className="st-root"><style>{style}</style><div className="loading-wrap"><div className="spinner" />Loading teachers…</div></div>;


  return (
    <div className="st-root">
      <style>{style}</style>

      <div className="st-header">
        <div><h1 className="st-title">All Teachers</h1><p className="st-subtitle">Platform-wide teaching staff across all schools.</p></div>
      </div>

      <div className="toolbar">
        <div className="search-wrap"><MagnifyingGlassIcon /><input className="search-input" placeholder="Search by name, subject, ID…" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        <div className="filter-wrap"><FunnelIcon /><select className="filter-select" value={selectedSchool} onChange={(e) => handleSchoolFilter(e.target.value)}><option value="">All Schools</option>{sortByName(schools).map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}</select></div>
      </div>

      <div className="summary-row">
        <div className="summary-pill"><span className="pill-dot" style={{ background: '#10c98e' }} /><strong>{totalTeachers}</strong> total teachers</div>
        <div className="summary-pill"><span className="pill-dot" style={{ background: '#3b6ef8' }} /><strong>{schools.length}</strong> schools</div>
        {selectedSchool && <div className="summary-pill"><span className="pill-dot" style={{ background: '#8b5cf6' }} />Filtered: <strong>{schools.find((s) => s._id === selectedSchool)?.name}</strong></div>}
        {search && <div className="summary-pill"><span className="pill-dot" style={{ background: '#f59e0b' }} /><strong>{filtered.length}</strong> results for "{search}"</div>}
      </div>

      <div className="table-panel">
        <div className="st-tbl-wrap">
        <table className="teachers-table">
          <thead><tr><th>Teacher</th><th>Email</th><th>School</th><th>Employee ID</th><th>Subjects</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6}><div className="empty-state"><div className="empty-icon"><AcademicCapIcon /></div><div className="empty-title">{search ? 'No teachers match your search' : 'No teachers found'}</div><div className="empty-sub">{search ? 'Try a different keyword.' : 'Teachers will appear here once added.'}</div></div></td></tr>
            ) : filtered.map((teacher) => {
              const subjects = teacher.subjects || [];
              const visible = subjects.slice(0, MAX_SUBJECTS);
              const extra = subjects.length - MAX_SUBJECTS;
              return (
                <tr key={teacher._id}>
                  <td><div className="name-cell"><div className="avatar">{initials(teacher.user?.name)}</div><span className="name-text">{teacher.user?.name || <span style={{ color: '#c0c5dc' }}>—</span>}</span></div></td>
                  <td><div className="info-cell"><EnvelopeIcon /><span className="mono">{teacher.user?.email || <span style={{ color: '#c0c5dc' }}>—</span>}</span></div></td>
                  <td><div className="info-cell"><BuildingOfficeIcon />{teacher.school?.name || <span style={{ color: '#c0c5dc' }}>—</span>}</div></td>
                  <td>{teacher.employeeId ? <span className="chip-id">{teacher.employeeId}</span> : <span style={{ color: '#c0c5dc', fontSize: '0.82rem' }}>—</span>}</td>
                  <td>{subjects.length > 0 ? <div className="subjects-cell">{visible.map((s, i) => <span key={i} className="subject-tag">{s}</span>)}{extra > 0 && <span className="subject-tag-more">+{extra}</span>}</div> : <span style={{ color: '#c0c5dc', fontSize: '0.82rem' }}>—</span>}</td>
                  <td>
                    <div className="action-btns">
                      <button
                      className={`btn-toggle ${teacher.user?.isActive!==false?'deactivate':'activate'}`}
                      onClick={() => handleToggle(teacher.user?._id)}
                      disabled={toggling[teacher.user?._id]}
                      title={teacher.user?.isActive!==false?'Deactivate':'Activate'}>
                      {toggling[teacher.user?._id]?'…':teacher.user?.isActive!==false?<NoSymbolIcon />:<CheckCircleIcon />}
                    </button>
                    <button className="btn-edit" onClick={() => openEdit(teacher)} title="Edit"><PencilIcon /></button>
                      <button className="btn-delete" onClick={() => handleDelete(teacher.user?._id, teacher.user?.name)} title="Delete"><TrashIcon /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {/* Mobile cards */}
        <div className="st-cards">
          {filtered.length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><AcademicCapIcon /></div><div className="empty-title">{search ? 'No teachers match your search' : 'No teachers found'}</div><div className="empty-sub">{search ? 'Try a different keyword.' : 'Teachers will appear here once added.'}</div></div>
          ) : filtered.map((teacher) => {
            const subjects = teacher.subjects || [];
            const visible = subjects.slice(0, MAX_SUBJECTS);
            const extra = subjects.length - MAX_SUBJECTS;
            return (
              <div className="st-card" key={teacher._id}>
                <div className="st-card-top">
                  <div className="name-cell"><div className="avatar">{initials(teacher.user?.name)}</div><span className="name-text">{teacher.user?.name || <span style={{ color: '#c0c5dc' }}>—</span>}</span></div>
                </div>
                <div className="st-card-row"><span className="st-card-label">Email</span><span className="mono">{teacher.user?.email || <span style={{ color: '#c0c5dc' }}>—</span>}</span></div>
                <div className="st-card-row"><span className="st-card-label">School</span><span>{teacher.school?.name || <span style={{ color: '#c0c5dc' }}>—</span>}</span></div>
                <div className="st-card-row"><span className="st-card-label">Employee ID</span>{teacher.employeeId ? <span className="chip-id">{teacher.employeeId}</span> : <span style={{ color: '#c0c5dc', fontSize: '0.82rem' }}>—</span>}</div>
                <div className="st-card-row"><span className="st-card-label">Subjects</span>{subjects.length > 0 ? <div className="subjects-cell" style={{justifyContent:'flex-end'}}>{visible.map((s, i) => <span key={i} className="subject-tag">{s}</span>)}{extra > 0 && <span className="subject-tag-more">+{extra}</span>}</div> : <span style={{ color: '#c0c5dc', fontSize: '0.82rem' }}>—</span>}</div>
                <div className="st-card-actions">
                  <button
                    className={`btn-toggle ${teacher.user?.isActive!==false?'deactivate':'activate'}`}
                    onClick={() => handleToggle(teacher.user?._id)}
                    disabled={toggling[teacher.user?._id]}
                    title={teacher.user?.isActive!==false?'Deactivate':'Activate'}>
                    {toggling[teacher.user?._id]?'…':teacher.user?.isActive!==false?<NoSymbolIcon />:<CheckCircleIcon />}
                  </button>
                  <button className="btn-edit" onClick={() => openEdit(teacher)} title="Edit"><PencilIcon /></button>
                  <button className="btn-delete" onClick={() => handleDelete(teacher.user?._id, teacher.user?.name)} title="Delete"><TrashIcon /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="pagination">
        <span className="page-info">Page <strong>{page}</strong> of <strong>{totalPages}</strong>{totalTeachers > 0 && <> &nbsp;·&nbsp; {totalTeachers} teachers total</>}</span>
        <div className="page-btns">
          <button className="page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeftIcon />Prev</button>
          <button className="page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next<ChevronRightIcon /></button>
        </div>
      </div>

      {editTarget && createPortal(
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setEditTarget(null)}>
          <div className="modal-card">
            <div className="modal-header">
              <div><div className="modal-title">Edit Teacher</div><div className="modal-sub">{editTarget.school?.name}{editTarget.employeeId ? ` · ${editTarget.employeeId}` : ''}</div></div>
              <button className="modal-close" onClick={() => setEditTarget(null)}><XMarkIcon /></button>
            </div>
            <form onSubmit={handleUpdate}>
              <div className="field"><label>Full Name</label><input type="text" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} required /></div>
              <div className="field"><label>Email Address</label><input type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} required /></div>
              <div className="toggle-row">
                <div><div className="toggle-label">Account Status</div><div className="toggle-sub">{editData.isActive ? 'Teacher can log in' : 'Login disabled'}</div></div>
                <button type="button" className={`toggle ${editData.isActive ? 'on' : ''}`} onClick={() => setEditData({ ...editData, isActive: !editData.isActive })} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setEditTarget(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving…' : <><CheckIcon />Save Changes</>}</button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}